import { cn } from '@/lib/cn';

/**
 * The loading state: a tower crane carries a beam out, lowers it, and the
 * building beside it rises a floor at a time.
 *
 * Pure SVG and CSS keyframes (tailwind.config.js `crane-*` and `floor-*`), so it
 * costs nothing to download and animates on the compositor. With reduced motion
 * requested it stands still, showing the finished building.
 *
 * index.html carries a static copy for the moment before JavaScript arrives —
 * change both together.
 */

// Lattice zig-zags for the mast (vertical) and jib (horizontal).
const MAST = `M43 124 ${Array.from({ length: 12 }, (_, i) => `L${i % 2 === 0 ? 53 : 43} ${116 - i * 8}`).join(' ')}`;
const JIB = `M34 25 ${Array.from({ length: 36 }, (_, i) => `L${38 + i * 4} ${i % 2 === 0 ? 19 : 25}`).join(' ')}`;

const FLOORS = [
  { y: 108, animation: 'animate-floor-1' },
  { y: 92, animation: 'animate-floor-2' },
  { y: 76, animation: 'animate-floor-3' },
  { y: 60, animation: 'animate-floor-4' },
];

export function ConstructionLoader({
  label = 'Loading',
  fullScreen = false,
  className,
}: {
  label?: string;
  /** Fill the viewport (session check, first route) rather than the content area. */
  fullScreen?: boolean;
  className?: string;
}) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'flex flex-col items-center justify-center gap-3 animate-fade-in',
        fullScreen ? 'min-h-screen bg-cloud' : 'min-h-[60vh]',
        className,
      )}
    >
      <svg
        viewBox="0 0 200 140"
        aria-hidden
        className="w-40 motion-reduce:[&_*]:!animate-none sm:w-48"
        fill="none"
      >
        {/* Ground */}
        <path d="M16 124 H184" className="stroke-line" strokeWidth="2" strokeLinecap="round" />
        <path d="M24 130 H40 M64 130 H92 M120 130 H132 M156 130 H176" className="stroke-line" strokeWidth="1.5" strokeLinecap="round" />

        {/* Building, one floor at a time */}
        {FLOORS.map((floor, index) => (
          <g key={floor.y} className={cn('origin-bottom [transform-box:fill-box]', floor.animation)}>
            <rect x="104" y={floor.y} width="52" height="16" className={index === 0 ? 'fill-navy' : 'fill-digital'} />
            <rect x="102" y={floor.y} width="56" height="2" className="fill-midnight" />
            {[111, 126, 141].map((x) => (
              <rect key={x} x={x} y={floor.y + 6} width="8" height="6" rx="0.75" className="fill-white/80" />
            ))}
          </g>
        ))}

        {/* Crane: base, mast, cab, jib, ties, counterweight */}
        <rect x="37" y="120" width="22" height="4" rx="1" className="fill-navy" />
        <path d={MAST} className="stroke-navy" strokeWidth="1.25" strokeLinejoin="round" />
        <path d="M43 124 V25 M53 124 V25" className="stroke-navy" strokeWidth="1.75" />
        <path d="M45 19 L48 7 L51 19" className="stroke-navy" strokeWidth="1.75" strokeLinejoin="round" />
        <path d="M48 7 L22 19 M48 7 L150 19" className="stroke-navy" strokeWidth="0.75" />
        <path d="M20 19 H178 M20 25 H178" className="stroke-navy" strokeWidth="1.75" strokeLinecap="round" />
        <path d={JIB} className="stroke-navy" strokeWidth="1" strokeLinejoin="round" />
        <rect x="20" y="25" width="14" height="9" rx="1" className="fill-midnight" />
        <rect x="53" y="26" width="10" height="9" rx="1.5" className="fill-navy" />
        <rect x="56" y="28" width="5" height="3.5" rx="0.5" className="fill-gold/80" />
        <circle cx="48" cy="6" r="2.25" className="fill-gold animate-crane-beacon" />

        {/* Trolley runs out along the jib; the hook lowers the beam and comes back empty */}
        <g className="animate-crane-trolley">
          <rect x="66" y="25" width="10" height="4" rx="1" className="fill-navy" />
          <path d="M71 29 V44" className="stroke-navy origin-top [transform-box:fill-box] animate-crane-cable" strokeWidth="1" />
          <g className="animate-crane-load">
            <path d="M71 44 v3.5 a2.5 2.5 0 1 1 -2.5 2.5" className="stroke-gold-700" strokeWidth="1.5" strokeLinecap="round" />
            <g className="animate-crane-beam">
              <path d="M71 50 L61 57 M71 50 L81 57" className="stroke-navy" strokeWidth="0.75" />
              <rect x="57" y="57" width="28" height="5" rx="1" className="fill-gold" />
              <path d="M61 59.5 H81" className="stroke-gold-700" strokeWidth="0.75" strokeDasharray="2 2" />
            </g>
          </g>
        </g>
      </svg>
      <div className="text-center">
        <p className="font-display text-xl font-semibold tracking-tight text-ink">
          One<span className="text-accent">Paracon</span>
        </p>
        <p className="mt-1 text-helper font-semibold uppercase tracking-[0.12em] text-muted">{label}</p>
      </div>
    </div>
  );
}
